import { codeExplanations } from '@/data';

/**
 * In-browser emulation of the OP API, used when `isStandaloneMode()` is true.
 *
 * It has the same signature as `fetch` and resolves a real `Response` so the
 * `RequestAPI` class works unchanged. The fake backend state (registered user,
 * found keys, sent virals) is kept in the sessionStorage: a refresh keeps the
 * progress, a new tab starts a new journey (no relog).
 *
 * Every endpoint answers 200 (unknown endpoints included), except the two
 * business answers the front-end relies on:
 * - `inscription/dedup/:email` 404 when the email is unknown (full register form)
 * - `qualif/register` 400 when the submitted code is invalid
 */

const STORAGE_KEY = 'louisxiii-standalone-api';

// Fake network latency, lets loaders and animations play as with the real API
const LATENCY = 300;

// Only the United States id (230) is used by the front-end (zip code rule)
const US_COUNTRY_ID = 230;
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad',
  'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Macau', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone',
  'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay',
  'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

// Consecutive ids in alphabetical order, aligned so that "United States" gets 230
const COUNTRY_ID_OFFSET = US_COUNTRY_ID - COUNTRIES.indexOf('United States');
const countryList = COUNTRIES.reduce((list, label, index) => {
  list[index + COUNTRY_ID_OFFSET] = label;
  return list;
}, {});

const randomHex = (length) => {
  let hex = '';
  while (hex.length < length) {
    hex += Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
  }
  return hex.slice(0, length);
};

const randomUid = () => {
  const hex = randomHex(32).toUpperCase();
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
};

// The sessionStorage can be unavailable (iframe, private mode...), keep an in-memory copy
let memoryDb = null;

const loadDb = () => {
  if (!memoryDb) {
    try {
      memoryDb = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    } catch (e) {
      console.log(e);
    }
  }

  if (!memoryDb) {
    memoryDb = {
      visitId: Math.floor(Math.random() * 100000) + 1,
      user: null,
      virals: [],
    };
  }

  return memoryDb;
};

const saveDb = (db) => {
  memoryDb = db;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  } catch (e) {
    console.log(e);
  }
};

const anonymousUser = () => ({
  Etape: 0,
  id_From: null,
  idu_email: null,
  uid: null,
  iduParrain: null,
  Nom: null,
  idU: null,
  oResponses: {},
  id_Civilite: null,
  telmobile: null,
  JeuOK: 0,
  oOptins: {},
  oDedup: {},
  Email: null,
  id_Declinaison_Culture: 1,
  Prenom: null,
});

const isSameEmail = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

const registerUser = (db, payload) => {
  // Same email: update the current user (dedup), otherwise create a new one
  const user = db.user && isSameEmail(db.user.Email, payload.sEmail) ? db.user : {
    ...anonymousUser(),
    idU: Math.floor(Math.random() * 100000) + 1,
    uid: randomUid(),
  };

  user.Email = payload.sEmail || user.Email;
  user.Prenom = payload.sFirstName || user.Prenom;
  user.Nom = payload.sLastName || user.Nom;
  user.id_Civilite = payload.iCivilId || user.id_Civilite;
  user.telmobile = payload.sMobile || user.telmobile;
  user.Etape = 1;
  user.oOptins = { ...user.oOptins, ...payload.oOptins };
  user.oResponses = { ...user.oResponses, ...payload.oResponses };
  user.idu_email = user.idU;

  db.user = user;
  saveDb(db);

  return user;
};

const tokenData = (db, payload) => ({
  _id: randomHex(32),
  expiredAt: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 19),
  finop: null,
  fromId: payload.iFromId || 0,
  iOpStatus: 1,
  iPageId: null,
  idU: null,
  oTagInfo: {},
  parrainId: payload.iParrainId || null,
  ueId: payload.iUeId || null,
  uid: null,
  visitId: db.visitId,
});

/**
 * Routes of the emulated API: `[method, endpoint pattern, handler]`.
 * Handlers receive `(db, payload, ...matches)` and return `[status, data]`.
 */
const ROUTES = [
  ['POST', /^session\/token$/, (db, payload) => [200, [tokenData(db, payload)]]],
  ['GET', /^session\/current$/, (db) => [200, [db.user || anonymousUser()]]],
  ['GET', /^session\/relog\/.*$/, (db, payload) => [200, [{ ...tokenData(db, payload), iPageId: 1 }]]],
  ['POST', /^inscription\/register$/, (db, payload) => {
    const user = registerUser(db, payload);
    return [200, [{ iIdU: user.idU, idU: user.idU, uid: user.uid, sUid: user.uid }]];
  }],
  ['POST', /^inscription\/register_or_relog$/, (db, payload) => {
    const user = registerUser(db, payload);
    return [200, [{ iIdU: user.idU, idU: user.idU, uid: user.uid, sUid: user.uid }]];
  }],
  ['GET', /^inscription\/suggestion\/.*$/, () => [200, [{ blocked: false, changed: false, suggestions: [] }]]],
  ['GET', /^inscription\/dedup\/(.*)$/, (db, payload, email) => {
    if (db.user && isSameEmail(db.user.Email, decodeURIComponent(email))) {
      return [200, [{ oOptins: db.user.oOptins }]];
    }
    return [404, ['User does not exist']];
  }],
  ['GET', /^inscription\/countries$/, () => [200, [countryList]]],
  ['POST', /^qualif\/register$/, (db, payload) => {
    const { sCode, ...oResponses } = payload.oResponses || {};

    if (sCode !== undefined) {
      const enigma = codeExplanations.find((item) => item.code === String(sCode).toLowerCase());
      if (!enigma) {
        return [400, ['Invalid code']];
      }
      oResponses[`iQuestion_${200 + enigma.id}`] = 1;
    }

    if (db.user) {
      db.user.oResponses = { ...db.user.oResponses, ...oResponses };
      saveDb(db);
    }

    return [200, ['ok']];
  }],
  ['POST', /^diffusion\/send_virals$/, (db, payload) => {
    const emails = payload.lEmails || [];
    const result = emails.reduce((final, email) => {
      final[email] = { code: 200 };
      return final;
    }, {});

    db.virals = [
      ...db.virals,
      ...emails.map((email) => ({ Email: email, id_UE_Envoi: Math.floor(Math.random() * 100000) + 1 })),
    ];
    saveDb(db);

    return [200, [result]];
  }],
  ['GET', /^diffusion\/summary_viral$/, (db) => [200, db.virals]],
  ['POST', /^track\/page_view$/, () => [200, [1]]],
];

const jsonResponse = (status, data) => new Response(
  JSON.stringify({
    status: status < 400 ? 'ok' : 'ko',
    code: status,
    format: 'json',
    locale: 'en_US',
    version: 'standalone',
    meta: { count: data.length },
    data,
  }),
  { status, headers: { 'Content-Type': 'application/json' } },
);

/**
 * Emulated `fetch` for the OP API.
 *
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export default function standaloneFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  // Remove `/api/v0/{opName}/{apiVersion}/` and query parameters
  const endpoint = new URL(url, document.location.href).pathname.replace(/^\/api\/v0\/[^/]+\/[^/]+\//, '');

  let payload = {};
  try {
    payload = (options.body && JSON.parse(options.body)) || {};
  } catch (e) {
    console.log(e);
  }

  const db = loadDb();
  let route = null;
  let matches = [];
  ROUTES.some(([routeMethod, pattern, handler]) => {
    const match = routeMethod === method && endpoint.match(pattern);
    if (match) {
      route = handler;
      matches = match.slice(1);
    }
    return !!match;
  });

  // Fallback: any other endpoint succeeds
  const [status, data] = route ? route(db, payload, ...matches) : [200, []];

  console.log(`[standalone-api] ${method} ${endpoint}`, status, data);

  return new Promise((resolve) => {
    setTimeout(() => resolve(jsonResponse(status, data)), LATENCY);
  });
}
